import { Target, Compass, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { COMPANY, ESTEEMED_CLIENTS, ABOUT_IMAGE } from "../data/content";

const VALUES = [
  {
    icon: Target,
    label: "Innovation",
    text: "Continuously evaluating emerging technologies to deliver future-ready security architectures.",
  },
  {
    icon: Compass,
    label: "Integrity",
    text: "Transparent engineering, honest pricing and uncompromising adherence to compliance standards.",
  },
  {
    icon: Users,
    label: "Customer-Centricity",
    text: "Every system is shaped around your operational reality — not the other way around.",
  },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="bg-slate-950 text-slate-100 min-h-screen">
      <PageHeader
        eyebrow="/ About Us"
        title={
          <>
            Securing tomorrow,
            <br />
            <span className="text-slate-500">engineered today.</span>
          </>
        }
        subtitle="Founded in February 2011 by Akash Thakker, GenX Technologies has spent over a decade designing integrated security and automation systems for enterprises, hospitals, government bodies and infrastructure clients."
        image={ABOUT_IMAGE}
      />

      {/* Founder + Mission */}
      <section className="py-20 md:py-28 px-6 md:px-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-4">
              / Our Story
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-50 mb-6">
              Built by an industry veteran.
            </h2>
            <p className="text-base text-slate-400 leading-relaxed mb-6">
              Under the leadership of {COMPANY.founder}, who brings {COMPANY.experienceYears} years
              of hands-on industry experience, GenX has grown into a trusted partner for high-stakes
              security deployments — from municipal corporations to pharmaceutical campuses.
            </p>
            <p className="text-base text-slate-400 leading-relaxed">
              We believe that security shouldn't be a stack of disconnected boxes. Every camera,
              biometric reader, fire panel and automation controller we deploy is designed to talk
              to the others — so you get one unified, intelligent system instead of ten silos.
            </p>
            <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 mt-8">
              <div className="bg-slate-950 p-5">
                <div className="text-3xl font-medium text-cyan-400">2011</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                  Established
                </div>
              </div>
              <div className="bg-slate-950 p-5">
                <div className="text-3xl font-medium text-cyan-400">15 yrs</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                  Founder Experience
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-slate-900 p-8 md:p-10 tracing-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-mono mb-4">
                Our Mission
              </div>
              <p className="text-xl md:text-2xl tracking-tight text-slate-100 leading-snug font-medium">
                "To deliver seamless, cutting-edge security integration that
                eliminates the friction of legacy systems — protecting people, assets
                and operations with intelligence built for tomorrow."
              </p>
              <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono">
                — {COMPANY.founder}, Founder
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 px-6 md:px-10 border-b border-white/5 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-4">
                / Our Values
              </div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-50">
                What we stand for.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {VALUES.map((v) => (
              <div
                key={v.label}
                data-testid={`value-${v.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-slate-950 p-8 md:p-10 group hover:bg-slate-900 transition-colors"
              >
                <div className="h-11 w-11 border border-cyan-400/30 bg-cyan-400/5 flex items-center justify-center mb-6 group-hover:border-cyan-400 transition-colors">
                  <v.icon className="h-5 w-5 text-cyan-300" strokeWidth={1.6} />
                </div>
                <h3 className="text-xl font-medium text-slate-100 tracking-tight mb-3">
                  {v.label}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      {/* CTA */}
      <section className="py-20 md:py-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h3 className="text-2xl md:text-3xl tracking-tight font-medium text-slate-100">
            Want to know how we can help your facility?
          </h3>
          <Link to="/contact">
            <Button className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-12 px-7 btn-glow group">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
